import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { Task } from '@/types/tasks';
import { toast } from 'sonner';
import { fetchAssignedTasks, completeTask, uncompleteTask, deleteTask, updateTask } from './database-tasks';
import { mapDbTaskToUiTask, TaskUpdate } from './types';
import { getDateRange } from './operations/task-date-utils';
import { DateRangeOption } from './types/task-operations-types';

// Stable empty arrays to prevent infinite re-renders
const EMPTY_ACTIVE_TASKS: Task[] = [];
const EMPTY_COMPLETED_TASKS: Task[] = [];

interface TaskManagerTaskCreationData {
  title: string;
  description?: string;
  dueDate?: string;
  priority?: 'high' | 'medium' | 'not-important' | null;
}

/**
 * Dedicated task operations hook for TaskManager page ONLY
 * Fetches only tasks assigned to the current user using database-level filtering
 * Completely isolated from project system to prevent cache conflicts
 */
export const useTaskManagerOperations = (
  dateRange: DateRangeOption = 'all',
  currentDate: Date = new Date(),
  showOnlyCurrentRange: boolean = false
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Calculate date range parameters - only needed for "Now" mode caching
  // "All" mode ignores date ranges completely
  const dateRangeParams = showOnlyCurrentRange 
    ? null  // "Now" mode doesn't need date range params
    : null; // "All" mode doesn't need date range params
    
  // Debug date range calculation
  console.log('[useTaskManagerOperations] Date range calculation:', {
    dateRange,
    currentDate: currentDate.toISOString(),
    showOnlyCurrentRange,
    calculatedRange: dateRangeParams
  });

  // Dynamic query key based on actual filtering behavior
  const queryKey = useMemo(() => {
    if (showOnlyCurrentRange) {
      // "Now" mode: Tasks within selected time period
      const dateRangeResult = getDateRange(dateRange, currentDate);
      if (dateRangeResult) {
        return ['task-manager-tasks', user?.id, 'now', dateRange, dateRangeResult.startDate, dateRangeResult.endDate];
      } else {
        const today = currentDate.toISOString().split('T')[0];
        return ['task-manager-tasks', user?.id, 'now', 'today', today];
      }
    } else {
      // "All" mode: All tasks regardless of date
      return ['task-manager-tasks', user?.id, 'all'];
    }
  }, [user?.id, showOnlyCurrentRange, dateRange, currentDate]);
  
  // Query for fetching ONLY assigned tasks using database-level filtering
  const { 
    data: tasks,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user?.id) return { active: [], completed: [] };
      
      try {
        console.log('[useTaskManagerOperations] Fetching assigned tasks for user:', user.id);
        
        // Use dedicated assigned tasks query - database does the filtering
        const { data, error } = await fetchAssignedTasks(user.id);
        
        if (error) {
          console.error('[useTaskManagerOperations] Failed to fetch assigned tasks:', error.message);
          setError(error.message);
          return { active: [], completed: [] };
        }
        
        if (!data) {
          console.log('[useTaskManagerOperations] No assigned tasks found');
          return { active: [], completed: [] };
        }

        console.log('[useTaskManagerOperations] Successfully fetched', data.length, 'assigned tasks');

        // Map DB tasks to UI tasks
        const mappedTasks = data.map(mapDbTaskToUiTask);
        
        // Enhanced debugging: Log task due dates for analysis
        console.log('[useTaskManagerOperations] Task due dates:', mappedTasks.map(task => ({
          id: task.id,
          title: task.title,
          dueDate: task.dueDate,
          isCompleted: task.isCompleted
        })));
        
        // Apply robust date filtering based on All/Now mode
        let filteredTasks = mappedTasks;
        
        if (showOnlyCurrentRange) {
          // "Now" mode: Show tasks within the selected time period (Day/Week/Month) + tasks without due dates
          const dateRangeResult = getDateRange(dateRange, currentDate);
          
          if (dateRangeResult) {
            const { startDate, endDate } = dateRangeResult;
            filteredTasks = mappedTasks.filter(task => {
              // Include tasks with no due date OR tasks within the selected time period
              if (!task.dueDate) return true;
              return task.dueDate >= startDate && task.dueDate <= endDate;
            });
            
            console.log('[useTaskManagerOperations] Applied "Now" filter with date range:', { 
              dateRange,
              period: `${startDate} to ${endDate}`,
              originalCount: mappedTasks.length, 
              filteredCount: filteredTasks.length,
              taskDetails: mappedTasks.map(t => ({ 
                title: t.title, 
                dueDate: t.dueDate, 
                included: !t.dueDate || (t.dueDate >= startDate && t.dueDate <= endDate)
              }))
            });
          } else {
            // Fallback to "today only" if date range calculation fails
            const today = currentDate.toISOString().split('T')[0];
            filteredTasks = mappedTasks.filter(task => {
              return !task.dueDate || task.dueDate === today;
            });
            
            console.log('[useTaskManagerOperations] Applied "Now" filter (today only - fallback):', { 
              today,
              originalCount: mappedTasks.length, 
              filteredCount: filteredTasks.length
            });
          }
        } else {
          // "All" mode: Show ALL tasks regardless of date range
          // This is the correct interpretation of the "All" button
          filteredTasks = mappedTasks;
          console.log('[useTaskManagerOperations] Applied "All" filter (no date filtering):', { 
            showOnlyCurrentRange, 
            originalCount: mappedTasks.length, 
            filteredCount: filteredTasks.length,
            taskDetails: mappedTasks.map(t => ({ title: t.title, dueDate: t.dueDate, isCompleted: t.isCompleted }))
          });
        }
        
        const active = filteredTasks.filter(task => !task.isCompleted);
        const completed = filteredTasks.filter(task => task.isCompleted);
        
        console.log('[useTaskManagerOperations] Split into:', { active: active.length, completed: completed.length });
        
        return { active, completed };
      } catch (err) {
        console.error('[useTaskManagerOperations] Unexpected error:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        return { active: [], completed: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 0,
    gcTime: 60000,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Stabilize array references
  const activeTasks = useMemo(() => 
    tasks?.active || EMPTY_ACTIVE_TASKS, 
    [tasks?.active]
  );
  
  const completedTasks = useMemo(() => 
    tasks?.completed || EMPTY_COMPLETED_TASKS, 
    [tasks?.completed]
  );

  // Simple task creation mutation using create_task_simple
  const createTaskMutation = useMutation({
    mutationFn: async (taskData: TaskManagerTaskCreationData) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase.rpc('create_task_simple', {
        p_title: taskData.title,
        p_description: taskData.description || null,
        p_due_date: taskData.dueDate || null,
        p_priority: taskData.priority || null
      });

      if (error) {
        console.error('Failed to create task:', error);
        throw new Error(error.message);
      }

      if (!data) {
        throw new Error('No response from server');
      }

      // Parse JSON response
      const result = typeof data === 'string' ? JSON.parse(data) : data;

      if (!result.success) {
        throw new Error(result.error || 'Task creation failed');
      }

      return result;
    },
    onSuccess: () => {
      // Invalidate ALL TaskManager queries with pattern matching
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && 
                 key[0] === 'task-manager-tasks' && 
                 key[1] === user?.id;
        }
      });
      
      // Also invalidate navbar task count (assigned-tasks-count)
      queryClient.invalidateQueries({ 
        queryKey: ['assigned-tasks-count', user?.id],
        exact: false 
      });
      
      toast.success('Task created successfully!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create task: ${error.message}`);
    }
  });

  // Task completion mutations
  const completeTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { data, error } = await completeTask(taskId);
      if (error) throw new Error(error.message);
      return data ? mapDbTaskToUiTask(data) : null;
    },
    onSuccess: () => {
      // Invalidate ALL TaskManager queries with pattern matching
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && 
                 key[0] === 'task-manager-tasks' && 
                 key[1] === user?.id;
        }
      });
      
      // Also invalidate navbar task count (assigned-tasks-count)
      queryClient.invalidateQueries({ 
        queryKey: ['assigned-tasks-count', user?.id],
        exact: false 
      });
    },
    onError: (error: Error) => {
      toast.error(`Failed to complete task: ${error.message}`);
    }
  });

  const uncompleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { data, error } = await uncompleteTask(taskId);
      if (error) throw new Error(error.message);
      return data ? mapDbTaskToUiTask(data) : null;
    },
    onSuccess: () => {
      // Invalidate ALL TaskManager queries with pattern matching
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && 
                 key[0] === 'task-manager-tasks' && 
                 key[1] === user?.id;
        }
      });
      
      // Also invalidate navbar task count (assigned-tasks-count)
      queryClient.invalidateQueries({ 
        queryKey: ['assigned-tasks-count', user?.id],
        exact: false 
      });
    },
    onError: (error: Error) => {
      toast.error(`Failed to uncomplete task: ${error.message}`);
    }
  });

  // Task deletion mutation
  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await deleteTask(taskId);
      if (error) throw new Error(error.message);
      return taskId;
    },
    onSuccess: () => {
      // Invalidate ALL TaskManager queries with pattern matching
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && 
                 key[0] === 'task-manager-tasks' && 
                 key[1] === user?.id;
        }
      });
      
      // Also invalidate navbar task count (assigned-tasks-count)
      queryClient.invalidateQueries({ 
        queryKey: ['assigned-tasks-count', user?.id],
        exact: false 
      });
      
      toast.success('Task deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete task: ${error.message}`);
    }
  });

  // Task update mutation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ 
      taskId, 
      updates, 
      silent = false 
    }: { 
      taskId: string, 
      updates: Partial<Task>, 
      silent?: boolean 
    }) => {
      // Convert UI task updates to DB task updates
      const dbUpdates: TaskUpdate = {};
      
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description ?? null;
      if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate ?? null;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority ?? null;
      if (updates.isCompleted !== undefined) dbUpdates.is_completed = updates.isCompleted;
      if (updates.cardOrder !== undefined) dbUpdates.card_order = updates.cardOrder;
      if (updates.goalId !== undefined) dbUpdates.goal_id = updates.goalId ?? null;

      const { data, error } = await updateTask(taskId, dbUpdates);
      if (error) throw new Error(error.message);
      
      return { 
        task: data ? mapDbTaskToUiTask(data) : null,
        silent
      };
    },
    onSuccess: ({ silent }) => {
      // Invalidate ALL TaskManager queries with pattern matching
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && 
                 key[0] === 'task-manager-tasks' && 
                 key[1] === user?.id;
        }
      });

      // Also invalidate navbar task count (assigned-tasks-count)
      queryClient.invalidateQueries({ 
        queryKey: ['assigned-tasks-count', user?.id],
        exact: false 
      });

      if (!silent) {
        toast.success('Task updated successfully');
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to update task: ${error.message}`);
    }
  });

  // Convenient wrapper functions
  const addTask = useCallback((taskData: TaskManagerTaskCreationData) => {
    createTaskMutation.mutate(taskData);
  }, [createTaskMutation]);

  const completeTaskAction = useCallback((taskId: string) => {
    completeTaskMutation.mutate(taskId);
  }, [completeTaskMutation]);

  const uncompleteTaskAction = useCallback((taskId: string) => {
    uncompleteTaskMutation.mutate(taskId);
  }, [uncompleteTaskMutation]);

  const removeTask = useCallback((taskId: string) => {
    deleteTaskMutation.mutate(taskId);
  }, [deleteTaskMutation]);

  const updateTaskAction = useCallback((taskId: string, updates: Partial<Task>, silent = false) => {
    updateTaskMutation.mutate({ taskId, updates, silent });
  }, [updateTaskMutation]);

  // NEW: Immediate update functions for drag operations that need instant persistence
  const updateTaskOrderAction = useCallback((taskId: string, newOrder: number) => {
    console.log('[TaskManagerOperations] Immediate task order update:', { taskId, newOrder });
    updateTaskMutation.mutate({ 
      taskId, 
      updates: { cardOrder: newOrder }, 
      silent: true // Silent for drag operations
    });
  }, [updateTaskMutation]);

  const immediateUpdateTaskAction = useCallback((taskId: string, updates: Partial<Task>, silent = true) => {
    console.log('[TaskManagerOperations] Immediate task update:', { taskId, updates });
    updateTaskMutation.mutate({ taskId, updates, silent });
  }, [updateTaskMutation]);

  // NEW: Task manager operations object for drag hook
  const taskManagerOperations = useMemo(() => ({
    updateTask: immediateUpdateTaskAction,
    updateTaskOrder: updateTaskOrderAction
  }), [immediateUpdateTaskAction, updateTaskOrderAction]);

  return {
    // Data
    activeTasks,
    completedTasks,
    isLoading,
    isError,
    error,
    
    // Actions
    addTask,
    completeTask: completeTaskAction,
    uncompleteTask: uncompleteTaskAction,
    removeTask,
    updateTask: updateTaskAction,
    refetch,
    
    // NEW: Drag-specific operations
    taskManagerOperations,
    updateTaskOrder: updateTaskOrderAction,
    immediateUpdateTask: immediateUpdateTaskAction,
    
    // Mutation states
    isCreating: createTaskMutation.isPending,
    isUpdating: updateTaskMutation.isPending || completeTaskMutation.isPending || uncompleteTaskMutation.isPending,
    isDeleting: deleteTaskMutation.isPending
  };
}; 